export type JsonRange = { from: number; to: number };

/**
 * Minimal structural view of a CodeMirror/Lezer `SyntaxNode` — just the members
 * the resolver below uses. Typed structurally so this stays decoupled from the
 * `@lezer/common` package (which isn't a direct dependency).
 */
interface SyntaxNodeLike {
  name: string;
  from: number;
  to: number;
  firstChild: SyntaxNodeLike | null;
  nextSibling: SyntaxNodeLike | null;
  getChild(type: string): SyntaxNodeLike | null;
  getChildren(type: string): SyntaxNodeLike[];
}

const VALUE_NODE_NAMES = new Set([
  'Object',
  'Array',
  'String',
  'Number',
  'True',
  'False',
  'Null',
]);

const valueChildren = (node: SyntaxNodeLike): SyntaxNodeLike[] => {
  const children: SyntaxNodeLike[] = [];
  for (let child = node.firstChild; child !== null; child = child.nextSibling) {
    if (VALUE_NODE_NAMES.has(child.name)) {
      children.push(child);
    }
  }
  return children;
};

/**
 * Resolves a JSON path (e.g. `['deliverables', 0, 'type']`) to a character
 * range using CodeMirror's JSON syntax tree, so a diagnostic can underline the
 * exact node. When a segment can't be resolved — a missing key or an
 * out-of-range index — it points at the start of the closest container so the
 * mark still lands near the problem rather than disappearing.
 */
export const resolveJsonPath = (
  root: SyntaxNodeLike,
  sliceDoc: (from: number, to: number) => string,
  path: (string | number)[]
): JsonRange | null => {
  const firstValue = valueChildren(root)[0];
  if (firstValue === undefined) {
    return null;
  }
  let current: SyntaxNodeLike = firstValue;

  for (const segment of path) {
    if (typeof segment === 'string') {
      if (current.name !== 'Object') {
        return { from: current.from, to: current.from + 1 };
      }
      let matched: SyntaxNodeLike | null = null;
      for (const property of current.getChildren('Property')) {
        const nameNode = property.getChild('PropertyName');
        if (nameNode === null) {
          continue;
        }
        let key: unknown;
        try {
          key = JSON.parse(sliceDoc(nameNode.from, nameNode.to));
        } catch {
          continue;
        }
        if (key === segment) {
          matched = property;
          break;
        }
      }
      if (matched === null) {
        return { from: current.from, to: current.from + 1 };
      }
      const propertyValues = valueChildren(matched);
      const value = propertyValues[propertyValues.length - 1];
      if (value === undefined) {
        const nameNode = matched.getChild('PropertyName');
        return nameNode === null
          ? { from: matched.from, to: matched.to }
          : { from: nameNode.from, to: nameNode.to };
      }
      current = value;
    } else {
      if (current.name !== 'Array') {
        return { from: current.from, to: current.from + 1 };
      }
      const item = valueChildren(current)[segment];
      if (item === undefined) {
        return { from: current.from, to: current.from + 1 };
      }
      current = item;
    }
  }

  return { from: current.from, to: current.to };
};
